import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, MessageSquare, CheckCheck, Download, Upload, X,
  AlertCircle, CheckCircle2, Loader2, Eye, Info, RotateCcw,
  ImagePlus, Copy, Check, Trash2, Search, ChevronLeft, ChevronRight,
} from 'lucide-react';
import api, { isAuthenticated } from '../utils/api';

const SENT_STATUS_KEY = 'whatsapp_sent_status';
const SENT_EXPIRY_MS = 24 * 60 * 60 * 1000; // ✅ 24 hours
const TEMPLATE_STORAGE_KEY = 'crm_message_template';
const BROADCAST_IMAGE_KEY = 'crm_broadcast_image';
const SENT_COUNT_KEY = 'whatsapp_sent_count'; // ✅ total unique messages sent
const PAGE_SIZE = 50;

const CSV_COLUMNS = ['customer_id', 'name', 'phone', 'gender', 'recent_purchase', 'notes'];

const DEFAULT_TEMPLATE = `Hi {name},

We hope you are doing well! 😊

{recent_purchase_line}

We would love to have you visit us again at *Ahmedabad NX* 🛍️

Tap to explore our new arrivals!`;

const RECENT_PURCHASE_PLACEHOLDER = `Your recent purchase with us: *{recent_purchase}* — we hope you loved it! 🌸

`;

// ── helpers ───────────────────────────────────────────────────────────────────

function loadSentStatus() {
  try {
    const raw = localStorage.getItem(SENT_STATUS_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    const now = Date.now();
    const cleaned = {};
    for (const [id, ts] of Object.entries(data)) {
      if (now - ts < SENT_EXPIRY_MS) cleaned[id] = ts;
    }
    return cleaned;
  } catch { return {}; }
}

function saveSentStatus(statusMap) {
  try { localStorage.setItem(SENT_STATUS_KEY, JSON.stringify(statusMap)); } catch {}
}

function loadSentCount() {
  try { return parseInt(localStorage.getItem(SENT_COUNT_KEY) || '0', 10); } catch { return 0; }
}

function saveSentCount(count) {
  try { localStorage.setItem(SENT_COUNT_KEY, String(count)); } catch {}
}

function loadTemplate() {
  try { return localStorage.getItem(TEMPLATE_STORAGE_KEY) || DEFAULT_TEMPLATE; }
  catch { return DEFAULT_TEMPLATE; }
}

function saveTemplate(t) {
  try { localStorage.setItem(TEMPLATE_STORAGE_KEY, t); } catch {}
}

function loadBroadcastImage() {
  try { return localStorage.getItem(BROADCAST_IMAGE_KEY) || null; } catch { return null; }
}

function saveBroadcastImage(base64) {
  try {
    if (base64) localStorage.setItem(BROADCAST_IMAGE_KEY, base64);
    else localStorage.removeItem(BROADCAST_IMAGE_KEY);
  } catch {}
}

function timeAgo(ts) {
  const diff = Math.floor((Date.now() - ts) / 60000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  return `${Math.floor(diff / 60)}h ago`;
}

function toCSV(rows) {
  const header = CSV_COLUMNS.join(',');
  const lines = rows.map(r =>
    CSV_COLUMNS.map(col => {
      const val = r[col] ?? '-';
      const str = String(val);
      return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
    }).join(',')
  );
  return [header, ...lines].join('\n');
}

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length < 2) throw new Error('CSV must have a header row + at least one data row.');
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const missing = ['name', 'phone'].filter(r => !headers.includes(r));
  if (missing.length) throw new Error(`Missing required columns: ${missing.join(', ')}`);
  return lines.slice(1).map((line) => {
    const values = [];
    let cur = '', inQ = false;
    for (let c = 0; c < line.length; c++) {
      const ch = line[c];
      if (ch === '"') { inQ = !inQ; continue; }
      if (ch === ',' && !inQ) { values.push(cur); cur = ''; continue; }
      cur += ch;
    }
    values.push(cur);
    const obj = {};
    headers.forEach((h, idx) => { obj[h] = values[idx]?.trim() || '-'; });
    return obj;
  }).filter(row => row.name && row.name !== '-');
}

function buildMessage(template, customer) {
  const salutation = customer.gender === 'Female' ? 'Mrs.' : customer.gender === 'Male' ? 'Mr.' : '';
  const fullName = `${salutation} ${customer.name}`.trim();
  const recentPurchaseLine = customer.recent_purchase
    ? RECENT_PURCHASE_PLACEHOLDER.replace('{recent_purchase}', customer.recent_purchase)
    : '';
  return template
    .replace(/\{name\}/g, fullName)
    .replace(/\{recent_purchase_line\}/g, recentPurchaseLine)
    .replace(/\{recent_purchase\}/g, customer.recent_purchase || '')
    .trim();
}

async function copyImageToClipboard(base64DataUrl) {
  const res = await fetch(base64DataUrl);
  const blob = await res.blob();
  let pngBlob = blob;
  if (blob.type !== 'image/png') pngBlob = await convertToPng(blob);
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
}

function convertToPng(blob) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      canvas.toBlob(resolve, 'image/png');
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ── Import Modal ──────────────────────────────────────────────────────────────

const ImportModal = ({ onClose, onSuccess }) => {
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const rows = parseCSV(ev.target.result);
        const sample = rows[0] || {};
        const warnings = [];
        if (!('customer_id' in sample) || sample.customer_id === '-')
          warnings.push('customer_id column absent — IDs will be auto-generated.');
        if (!('gender' in sample) || rows.every(r => r.gender === '-'))
          warnings.push('gender column absent — will show "—".');
        if (!('recent_purchase' in sample) || rows.every(r => r.recent_purchase === '-'))
          warnings.push('recent_purchase column absent — {recent_purchase_line} will be skipped.');
        if (!('notes' in sample) || rows.every(r => r.notes === '-'))
          warnings.push('notes column absent — will default to empty.');
        setPreview({ rows, warnings });
        setError('');
      } catch (err) {
        setError(err.message);
        setPreview(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!preview) return;
    setLoading(true);
    try {
      const payload = preview.rows.map(({ customer_id, ...rest }) => ({
        name: rest.name,
        phone: rest.phone,
        gender: rest.gender === '-' ? null : rest.gender,
        recent_purchase: rest.recent_purchase === '-' ? '' : rest.recent_purchase,
        notes: rest.notes === '-' ? '' : rest.notes,
      }));
      const response = await api.post('/customers/import', { customers: payload });
      setResult(response.data);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-lg w-full max-w-2xl shadow-xl border border-gray-200 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-[#2A0000] flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#E5A93C]" />Import Customers from CSV
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {!result && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
              <p className="font-semibold">Expected CSV columns:</p>
              <code className="block bg-white/60 px-2 py-1 rounded font-mono">name, phone, gender, recent_purchase, notes</code>
              <p><span className="font-semibold">Required:</span> name, phone &nbsp;|&nbsp; <span className="font-semibold">Optional:</span> gender, recent_purchase, notes, customer_id</p>
            </div>
          )}
          {!result && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-[#E5A93C] transition-colors" onClick={() => fileRef.current?.click()}>
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Click to choose a <span className="font-semibold">.csv</span> file</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleFile} />
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />{error}
            </div>
          )}
          {preview?.warnings?.length > 0 && !result && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-1">
              {preview.warnings.map((w, i) => (
                <p key={i} className="text-xs text-yellow-800 flex items-start gap-1">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{w}
                </p>
              ))}
            </div>
          )}
          {preview && !result && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Preview — <span className="text-[#2A0000]">{preview.rows.length} rows</span> detected</p>
              <div className="overflow-x-auto rounded border border-gray-200 max-h-52">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>{['name','phone','gender','recent_purchase','notes'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {preview.rows.slice(0, 10).map((row, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        {['name','phone','gender','recent_purchase','notes'].map(col => (
                          <td key={col} className={`px-3 py-2 ${row[col] === '-' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {row[col] === '-' ? '—' : row[col]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {preview.rows.length > 10 && <p className="text-xs text-gray-400 px-3 py-2 border-t">… and {preview.rows.length - 10} more rows</p>}
              </div>
            </div>
          )}
          {result && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
              <p className="text-lg font-bold text-[#2A0000]">Import Complete!</p>
              <div className="flex justify-center gap-6 text-sm">
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center">
                  <p className="text-2xl font-bold text-green-700">{result.created}</p>
                  <p className="text-green-600">Customers Added</p>
                </div>
                {result.skipped > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-center">
                    <p className="text-2xl font-bold text-yellow-700">{result.skipped}</p>
                    <p className="text-yellow-600">Skipped (duplicate phone)</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 text-gray-700">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && preview && (
            <button onClick={handleImport} disabled={loading}
              className="px-4 py-2 text-sm bg-[#2A0000] text-white rounded-sm hover:bg-[#3d0000] disabled:opacity-50 flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              Import {preview.rows.length} Customers
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── WhatsApp Preview Modal ────────────────────────────────────────────────────

const PreviewModal = ({ message, customerName, broadcastImage, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3" style={{ background: '#075E54' }}>
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {customerName.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-semibold text-sm truncate">{customerName}</p>
          <p className="text-white/60 text-[10px]">WhatsApp Preview</p>
        </div>
        <button onClick={onClose} className="text-white/70 hover:text-white flex-shrink-0"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-4 max-h-[60vh] overflow-y-auto" style={{ background: '#ECE5DD' }}>
        {broadcastImage && (
          <div className="bg-[#DCF8C6] rounded-xl rounded-tl-sm max-w-[92%] shadow-sm overflow-hidden mb-2 relative">
            <div className="absolute -left-2 top-0 w-0 h-0" style={{ borderTop: '8px solid #DCF8C6', borderLeft: '8px solid transparent' }} />
            <img src={broadcastImage} alt="Broadcast" className="w-full object-cover max-h-52" />
            <p className="text-[10px] text-gray-400 text-right px-3 py-1">
              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
            </p>
          </div>
        )}
        <div className="bg-[#DCF8C6] rounded-xl rounded-tl-sm px-4 py-3 max-w-[92%] shadow-sm relative">
          <div className="absolute -left-2 top-0 w-0 h-0" style={{ borderTop: '8px solid #DCF8C6', borderLeft: '8px solid transparent' }} />
          <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed break-words">{message}</pre>
          <p className="text-[10px] text-gray-400 text-right mt-2">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
          </p>
        </div>
      </div>
      <div className="px-4 py-3 border-t bg-gray-50 text-center space-y-1">
        <p className="text-xs text-gray-500">This is exactly how the message will appear in WhatsApp.</p>
        <button onClick={onClose} className="text-sm text-[#2A0000] font-semibold hover:underline">Close</button>
      </div>
    </div>
  </div>
);

// ── Broadcast Image Panel ─────────────────────────────────────────────────────

const BroadcastImagePanel = ({ broadcastImage, onImageChange }) => {
  const fileRef = useRef();
  const [copying, setCopying] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyError, setCopyError] = useState('');

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file.'); return; }
    if (file.size > 3 * 1024 * 1024) { alert('Image is too large. Please use an image under 3 MB.'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => onImageChange(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleCopy = async () => {
    if (!broadcastImage) return;
    setCopying(true); setCopyError('');
    try {
      await copyImageToClipboard(broadcastImage);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2500);
    } catch (err) {
      setCopyError('Copy failed. Your browser may not support clipboard image writing. Try right-clicking the image instead.');
    }
    setCopying(false);
  };

  const handleRemove = () => {
    if (window.confirm('Remove the broadcast image?')) { onImageChange(null); setCopyError(''); }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#2A0000]">Broadcast Image</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Upload a promotional banner. Copy it to clipboard before (or after) sending a WhatsApp message — then paste it directly in the chat.
          </p>
        </div>
        {broadcastImage && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={handleCopy} disabled={copying}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-sm transition-all ${copySuccess ? 'bg-green-500 text-white' : 'bg-[#2A0000] text-white hover:bg-[#3d0000]'} disabled:opacity-50`}>
              {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : copySuccess ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copySuccess ? 'Copied!' : 'Copy Image'}
            </button>
            <button onClick={handleRemove} className="p-2 rounded-sm border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors" title="Remove image">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      <div className="p-5">
        {!broadcastImage ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#E5A93C] transition-colors" onClick={() => fileRef.current?.click()}>
            <ImagePlus className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600">Click to upload a broadcast image</p>
            <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP — max 3 MB</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 max-h-64 flex items-center justify-center">
              <img src={broadcastImage} alt="Broadcast" className="max-h-64 w-full object-contain" />
            </div>
            {copyError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{copyError}</span>
              </div>
            )}
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              <Info className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <p className="text-[11px] text-amber-800">
                <span className="font-semibold">How to use:</span> Click <span className="font-semibold">Copy Image</span>, open WhatsApp, paste (<kbd className="bg-white/60 px-1 rounded border border-amber-300">Ctrl+V</kbd> / <kbd className="bg-white/60 px-1 rounded border border-amber-300">⌘V</kbd>) the image in the chat, then send it alongside your message.
              </p>
            </div>
            <button onClick={() => fileRef.current?.click()} className="text-xs text-gray-500 hover:text-[#2A0000] flex items-center gap-1 transition-colors">
              <ImagePlus className="w-3.5 h-3.5" /> Replace image
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
          </div>
        )}
      </div>
    </div>
  );
};

// ── Pagination Controls ───────────────────────────────────────────────────────

const PaginationControls = ({ page, totalPages, totalCustomers, onPageChange, loading }) => {
  const [goToInput, setGoToInput] = useState('');

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages;
  };

  const handleGoTo = () => {
    const num = parseInt(goToInput, 10);
    if (!isNaN(num) && num >= 1 && num <= totalPages) {
      onPageChange(num);
      setGoToInput('');
    }
  };

  return (
    <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-wrap gap-3">
      <p className="text-sm text-gray-500">
        Showing <span className="font-semibold text-[#2A0000]">{Math.min((page - 1) * PAGE_SIZE + 1, totalCustomers)}–{Math.min(page * PAGE_SIZE, totalCustomers)}</span> of <span className="font-semibold text-[#2A0000]">{totalCustomers.toLocaleString()}</span> customers
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Page number buttons */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => onPageChange(page - 1)}
            disabled={page === 1 || loading}
            className="p-1.5 rounded border border-gray-200 text-gray-500 hover:border-[#2A0000] hover:text-[#2A0000] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          {pageNumbers().map((p, i) =>
            p === '...'
              ? <span key={`ellipsis-${i}`} className="px-2 text-gray-400 text-sm select-none">…</span>
              : <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  disabled={loading}
                  className={`min-w-[32px] h-8 rounded text-sm font-medium border transition-colors ${
                    p === page
                      ? 'bg-[#2A0000] text-white border-[#2A0000]'
                      : 'border-gray-200 text-gray-600 hover:border-[#2A0000] hover:text-[#2A0000]'
                  } disabled:opacity-50`}
                >{p}</button>
          )}
          <button
            onClick={() => onPageChange(page + 1)}
            disabled={page === totalPages || loading}
            className="p-1.5 rounded border border-gray-200 text-gray-500 hover:border-[#2A0000] hover:text-[#2A0000] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Go to page */}
        <div className="flex items-center gap-1.5 border-l border-gray-200 pl-3">
          <span className="text-xs text-gray-400 whitespace-nowrap">Go to</span>
          <input
            type="number"
            min={1}
            max={totalPages}
            value={goToInput}
            onChange={e => setGoToInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleGoTo()}
            placeholder={String(page)}
            className="w-14 h-8 px-2 text-sm text-center border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-[#E5A93C] focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <button
            onClick={handleGoTo}
            disabled={loading || !goToInput}
            className="h-8 px-3 text-xs font-semibold bg-[#2A0000] text-white rounded hover:bg-[#3d0000] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Go
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

const AdminCRMPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(() => loadTemplate());
  const [sentStatus, setSentStatus] = useState(() => loadSentStatus());
  const [sentCount, setSentCount] = useState(() => loadSentCount());
  const [broadcastImage, setBroadcastImage] = useState(() => loadBroadcastImage());
  const [showImport, setShowImport] = useState(false);
  const [previewFor, setPreviewFor] = useState(null);
  const [templateSaved, setTemplateSaved] = useState(false);
  const [rowCopyState, setRowCopyState] = useState({});

  // ── Pagination & Search state ──
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const searchTimeout = useRef(null);

  const [, forceRender] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const id = setInterval(() => forceRender(n => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/admin/login'); return; }
  }, []);

  useEffect(() => {
    fetchCustomers(page, searchQuery);
  }, [page, searchQuery]);

  const fetchCustomers = async (p = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: PAGE_SIZE });
      if (search.trim()) params.append('search', search.trim());
      const response = await api.get(`/customers?${params.toString()}`);
      // Support both paginated { customers, total, total_pages } and legacy array
      const sortNewest = (arr) =>
        [...arr].sort((a, b) => String(b.customer_id).localeCompare(String(a.customer_id), undefined, { numeric: true }));

      if (Array.isArray(response.data)) {
        setCustomers(sortNewest(response.data));
        setTotalCustomers(response.data.length);
        setTotalPages(1);
      } else {
        setCustomers(sortNewest(response.data.customers || []));
        setTotalCustomers(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
    setLoading(false);
  };

  // Debounced search
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setPage(1);
      setSearchQuery(val);
    }, 400);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
    // Scroll table into view smoothly
    document.getElementById('customer-table-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSaveTemplate = () => {
    saveTemplate(template);
    setTemplateSaved(true);
    setTimeout(() => setTemplateSaved(false), 2000);
  };

  const handleResetTemplate = () => {
    if (window.confirm('Reset to default template?')) {
      setTemplate(DEFAULT_TEMPLATE);
      saveTemplate(DEFAULT_TEMPLATE);
    }
  };

  const handleExportCSV = () => {
    const csv = toCSV(customers);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleWhatsAppClick = (customer) => {
    const already = sentStatus[customer.customer_id];
    const isResend = already && Date.now() - already < SENT_EXPIRY_MS;
    if (isResend) {
      if (!window.confirm(`You already sent a WhatsApp message to ${customer.name} recently. Send again?`)) return;
    }
    const message = buildMessage(template, customer);
    window.open(`https://wa.me/${customer.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
    const updated = { ...sentStatus, [customer.customer_id]: Date.now() };
    setSentStatus(updated);
    saveSentStatus(updated);
    // Only count the very first send per customer (not re-sends)
    if (!isResend) {
      const newCount = sentCount + 1;
      setSentCount(newCount);
      saveSentCount(newCount);
    }
  };

  const handleRowCopyImage = async (customerId) => {
    if (!broadcastImage) return;
    setRowCopyState(prev => ({ ...prev, [customerId]: 'copying' }));
    try {
      await copyImageToClipboard(broadcastImage);
      setRowCopyState(prev => ({ ...prev, [customerId]: 'done' }));
      setTimeout(() => setRowCopyState(prev => ({ ...prev, [customerId]: 'idle' })), 2500);
    } catch {
      setRowCopyState(prev => ({ ...prev, [customerId]: 'error' }));
      setTimeout(() => setRowCopyState(prev => ({ ...prev, [customerId]: 'idle' })), 3000);
    }
  };

  const handleImageChange = (base64) => {
    setBroadcastImage(base64);
    saveBroadcastImage(base64);
  };

  const isSent = (customerId) => {
    const ts = sentStatus[customerId];
    return ts && Date.now() - ts < SENT_EXPIRY_MS;
  };

  const sampleCustomer = customers[0] || {
    name: 'Sample Customer', gender: 'Female',
    recent_purchase: 'Banarasi Saree', customer_id: 'preview',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-[#E5A93C] hover:text-[#2A0000]"><ArrowLeft className="w-6 h-6" /></Link>
            <h1 className="font-heading text-xl font-bold text-[#2A0000]">CRM & WhatsApp Broadcast</h1>
            {sentCount > 0 && (
              <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1">
                <MessageSquare className="w-3.5 h-3.5 text-[#25D366]" />
                <span className="text-xs font-semibold text-green-800">
                  {sentCount.toLocaleString()} sent
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImport(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-gray-50 text-gray-700 transition-colors">
              <Upload className="w-4 h-4" />Import CSV
            </button>
            <button onClick={handleExportCSV} disabled={customers.length === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-[#2A0000] text-white rounded-sm hover:bg-[#3d0000] disabled:opacity-40 transition-colors">
              <Download className="w-4 h-4" />Export CSV
            </button>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6">

        {/* ── Message Template Editor ── */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#2A0000]">Message Template</h2>
              <p className="text-xs text-gray-500 mt-0.5">Write your message once — it's sent to all customers with their name filled in automatically.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleResetTemplate}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded hover:bg-gray-50 transition-colors">
                <RotateCcw className="w-3 h-3" />Reset
              </button>
              <button onClick={handleSaveTemplate}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded transition-all ${
                  templateSaved ? 'bg-green-500 text-white' : 'bg-[#2A0000] text-white hover:bg-[#3d0000]'
                }`}>
                {templateSaved ? '✓ Saved' : 'Save Template'}
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-200">
            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">✏️ Write Template</p>
                <span className="text-[10px] text-gray-400">{template.length} chars</span>
              </div>
              <textarea
                value={template}
                onChange={e => setTemplate(e.target.value)}
                rows={14}
                spellCheck
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm font-mono leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#E5A93C] focus:border-transparent"
                placeholder="Write your message here..."
              />
              <div>
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Available Placeholders — click to insert</p>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { tag: '{name}', desc: "Customer's full name with salutation" },
                    { tag: '{recent_purchase_line}', desc: "Full 'Your recent purchase...' sentence (empty if no purchase)" },
                    { tag: '{recent_purchase}', desc: "Just the product name inline" },
                  ].map(({ tag, desc }) => (
                    <button key={tag} title={desc}
                      onClick={() => {
                        const ta = document.querySelector('textarea');
                        const start = ta.selectionStart; const end = ta.selectionEnd;
                        const newVal = template.slice(0, start) + tag + template.slice(end);
                        setTemplate(newVal);
                        setTimeout(() => { ta.focus(); ta.setSelectionRange(start + tag.length, start + tag.length); }, 0);
                      }}
                      className="text-[11px] font-mono px-2 py-0.5 rounded border border-[#E5A93C]/50 text-[#2A0000] bg-amber-50 hover:bg-[#E5A93C] hover:text-white hover:border-[#E5A93C] transition-colors">
                      {tag}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">
                  Use <code className="bg-gray-100 px-1 rounded">*text*</code> for bold in WhatsApp &nbsp;·&nbsp;
                  <code className="bg-gray-100 px-1 rounded">_text_</code> for italic
                </p>
              </div>
            </div>

            <div className="p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">👁 Live Preview</p>
                <button onClick={() => setPreviewFor(sampleCustomer)}
                  className="text-[11px] text-[#2A0000] font-medium hover:underline flex items-center gap-1">
                  <Eye className="w-3 h-3" />WhatsApp view
                </button>
              </div>
              <div className="rounded-xl overflow-hidden border border-gray-200 flex-1">
                <div className="flex items-center gap-2.5 px-3 py-2" style={{ background: '#075E54' }}>
                  <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {sampleCustomer.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold leading-none">{sampleCustomer.name}</p>
                    <p className="text-white/50 text-[9px]">online</p>
                  </div>
                </div>
                <div className="p-3 min-h-[200px]" style={{ background: '#ECE5DD' }}>
                  {broadcastImage && (
                    <div className="bg-[#DCF8C6] rounded-xl rounded-tl-sm max-w-[90%] shadow-sm overflow-hidden mb-2 relative">
                      <div className="absolute -left-1.5 top-0 w-0 h-0" style={{ borderTop: '6px solid #DCF8C6', borderLeft: '6px solid transparent' }} />
                      <img src={broadcastImage} alt="Broadcast" className="w-full object-cover max-h-32" />
                      <p className="text-[9px] text-gray-400 text-right px-2 py-1">
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
                      </p>
                    </div>
                  )}
                  <div className="bg-[#DCF8C6] rounded-xl rounded-tl-sm px-3 py-2.5 max-w-[90%] shadow-sm relative">
                    <div className="absolute -left-1.5 top-0 w-0 h-0" style={{ borderTop: '6px solid #DCF8C6', borderLeft: '6px solid transparent' }} />
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap font-sans leading-relaxed break-words">
                      {buildMessage(template, sampleCustomer)}
                    </pre>
                    <p className="text-[9px] text-gray-400 text-right mt-1">
                      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} ✓✓
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                <Info className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-[11px] text-blue-700 leading-relaxed">
                  Preview uses {customers.length > 0 ? `your first customer "${sampleCustomer.name}"` : 'a sample customer'}.
                  Click <span className="font-semibold">👁 on any row</span> in the table below to preview for that specific customer.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Broadcast Image ── */}
        <BroadcastImagePanel broadcastImage={broadcastImage} onImageChange={handleImageChange} />

        {/* ── Customer Table ── */}
        <div id="customer-table-section" className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-[#2A0000]">Customer Database</h2>
              <span className="text-sm text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full font-medium">
                {totalCustomers.toLocaleString()} total
              </span>
              {sentCount > 0 && (
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-800 bg-green-50 border border-green-200 px-2.5 py-0.5 rounded-full">
                    <CheckCheck className="w-3.5 h-3.5 text-[#25D366]" />
                    {sentCount.toLocaleString()} message{sentCount !== 1 ? 's' : ''} sent
                  </span>
                  <button
                    onClick={() => {
                      if (window.confirm('Reset the sent message count to 0?')) {
                        setSentCount(0);
                        saveSentCount(0);
                      }
                    }}
                    className="text-[10px] text-gray-400 hover:text-red-500 transition-colors underline underline-offset-2"
                    title="Reset count"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
            {/* Search bar */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchInput}
                onChange={handleSearchChange}
                placeholder="Search name, phone, ID, purchase…"
                className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E5A93C] focus:border-transparent"
              />
              {searchInput && (
                <button onClick={handleClearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 gap-3 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin text-[#E5A93C]" />
              <span className="text-sm">Loading customers…</span>
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-20 text-gray-500 text-sm">
              {searchQuery ? `No customers found for "${searchQuery}".` : 'No customers yet.'}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      {['Customer ID', 'Name', 'Phone', 'Gender', 'Recent Purchase', 'Action'].map(h => (
                        <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {customers.map((customer) => {
                      const sent = isSent(customer.customer_id);
                      const sentTs = sentStatus[customer.customer_id];
                      const copyState = rowCopyState[customer.customer_id] || 'idle';
                      return (
                        <tr key={customer.customer_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 text-sm font-mono text-gray-500">{customer.customer_id}</td>
                          <td className="px-6 py-4 font-medium text-[#2C2C2C]">{customer.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{customer.phone}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{customer.gender || '—'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{customer.recent_purchase || '—'}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <button onClick={() => setPreviewFor(customer)}
                                className="p-1.5 rounded border border-gray-200 text-gray-400 hover:text-[#2A0000] hover:border-gray-400 transition-colors"
                                title="Preview personalised message">
                                <Eye className="w-4 h-4" />
                              </button>
                              {broadcastImage && (
                                <button onClick={() => handleRowCopyImage(customer.customer_id)} disabled={copyState === 'copying'}
                                  title="Copy broadcast image to clipboard"
                                  className={`flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium border transition-colors ${
                                    copyState === 'done' ? 'bg-green-50 border-green-300 text-green-700'
                                    : copyState === 'error' ? 'bg-red-50 border-red-200 text-red-600'
                                    : 'bg-white border-gray-300 text-gray-600 hover:border-[#E5A93C] hover:text-[#2A0000]'
                                  } disabled:opacity-50`}>
                                  {copyState === 'copying' ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : copyState === 'done' ? <Check className="w-3.5 h-3.5" />
                                    : <Copy className="w-3.5 h-3.5" />}
                                  <span className="hidden sm:inline">
                                    {copyState === 'done' ? 'Copied!' : copyState === 'error' ? 'Failed' : 'Copy Image'}
                                  </span>
                                </button>
                              )}
                              <button onClick={() => handleWhatsAppClick(customer)}
                                className={`flex items-center gap-2 px-4 py-2 rounded-sm transition-colors text-sm text-white ${
                                  sent ? 'bg-[#128C7E] hover:bg-[#0d7066]' : 'bg-[#25D366] hover:bg-[#20BA5A]'
                                }`}>
                                <MessageSquare className="w-4 h-4" />
                                <span>WhatsApp</span>
                              </button>
                              {sent && (
                                <span className="inline-flex items-center gap-1 text-xs font-medium text-[#128C7E] bg-green-50 border border-green-200 px-2 py-1 rounded-full whitespace-nowrap select-none">
                                  <CheckCheck className="w-3.5 h-3.5" />Sent · {timeAgo(sentTs)}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <PaginationControls
                  page={page}
                  totalPages={totalPages}
                  totalCustomers={totalCustomers}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              )}
            </>
          )}
        </div>

        {/* Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2">💡 How to Use CRM</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>1. Write your message template once in the editor. Use <code className="bg-white/60 px-1 rounded font-mono">{'{name}'}</code> and <code className="bg-white/60 px-1 rounded font-mono">{'{recent_purchase_line}'}</code> as placeholders.</li>
            <li>2. Click <span className="font-semibold">Save Template</span> — the template is stored in your browser and stays across sessions.</li>
            <li>3. <span className="font-semibold">Upload a Broadcast Image</span> (optional) — a banner or promotional image to send alongside the message.</li>
            <li>4. Use <span className="font-semibold">👁</span> on any row to preview exactly what that customer will receive.</li>
            <li>5. Click <span className="font-semibold">Copy Image</span> on a row → open WhatsApp → paste the image → then click <span className="font-semibold">WhatsApp</span> to send the personalised text.</li>
            <li>6. A <span className="font-semibold">Sent ✓✓</span> badge stays for <span className="font-semibold">24 hours</span> to prevent duplicate sends.</li>
          </ul>
        </div>
      </div>

      {showImport && <ImportModal onClose={() => setShowImport(false)} onSuccess={() => { setPage(1); fetchCustomers(1, searchQuery); }} />}
      {previewFor && (
        <PreviewModal
          message={buildMessage(template, previewFor)}
          customerName={previewFor.name}
          broadcastImage={broadcastImage}
          onClose={() => setPreviewFor(null)}
        />
      )}
    </div>
  );
};

export default AdminCRMPage;